import React, { useState } from "react";
import { HardHat, Plus } from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { useSafety } from "../../../hooks/useSafety";

import SafetyForm from "../../modals/forms/SafetyForm";

const EMPTY_FORM = {
  observation_date: "",
  observation_type: "observation",
  category: "general",
  description: "",
  location: "",
  action_taken: "",
  status: "open",
};

const SafetyView = () => {
  const { activeProject } = useProjectContext();
  const { items, loading, creating, error, create } = useSafety();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  if (!activeProject) return null;

  const onChange = (field, val) => setForm((p) => ({ ...p, [field]: val }));

  const onSubmit = async (e) => {
    e.preventDefault();

    await create({
      ...form,
      observation_date: form.observation_date, // required
    });

    setShowForm(false);
    setForm(EMPTY_FORM);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Safety</h2>
          <p className="text-gray-600">
            Safety observations, incidents, and compliance tracking
          </p>
        </div>
        <HardHat className="w-8 h-8 text-yellow-600" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Project: <strong>{activeProject.name}</strong>
        </div>

        <button
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-yellow-600 text-white text-sm font-medium hover:bg-yellow-700"
        >
          <Plus className="w-4 h-4" />
          New Observation
        </button>
      </div>

      {/* Create Form (extracted) */}
      {showForm && (
        <SafetyForm
          value={form}
          onChange={onChange}
          onSubmit={onSubmit}
          onCancel={() => setShowForm(false)}
          creating={creating}
        />
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b text-sm font-medium text-gray-800">
          Safety Records
        </div>

        {loading && <div className="p-4 text-sm text-gray-500">Loading...</div>}
        {error && (
          <div className="p-4 text-sm text-red-600">
            Failed to load safety records.
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="p-4 text-sm text-gray-500">No safety records yet.</div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Location</th>
                  <th className="text-left p-3">Description</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t">
                    <td className="p-3">{s.observation_date}</td>
                    <td className="p-3">{s.observation_type || "-"}</td>
                    <td className="p-3">{s.status || "-"}</td>
                    <td className="p-3">{s.location || "-"}</td>
                    <td className="p-3">{s.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SafetyView;
